import React, { useEffect, useState } from 'react';
import { donate, rules, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Plus, Loader2, Image as ImageIcon, Trash2, Truck, PackageOpen, DollarSign } from 'lucide-react';
import RulesModal from '../components/RulesModal';
import AppImage from '../components/AppImage';
import SiteSections from '../components/SiteSections';

const DELIVERY_OPTIONS = [
  { value: 'pickup', label: 'Local pickup' },
  { value: 'shipping', label: 'Shipping (donor pays label + fee at checkout)' },
];

export default function Donate() {
  const { user, refresh } = useAuth();
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesStatus, setRulesStatus] = useState(null);
  const [mine, setMine] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', estimatedValue: '',
    images: [], deliveryMethod: 'pickup', deliveryFee: '',
    deliveryDescription: '',
    pickupInstructions: '',
    testAuction: false,
    // C.A.N. rebuild — itemized cash costs
    shippingCost: '', taxes: '', handling: '',
    productSize: '', productColor: '', productCondition: '',
    productBrand: '', productSpecs: '',
  });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [rs, m] = await Promise.all([rules.status(), donate.mine()]);
      setRulesStatus(rs);
      setMine(m);
      if (rs?.needsAcknowledgment) setRulesOpen(true);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const acceptRules = async () => {
    try {
      await rules.accept();
      setRulesOpen(false);
      toast({ title: 'Rules accepted', description: 'You can now submit listings.' });
      await load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.images.length >= 5) { toast({ title: 'Max 5 photos' }); return; }
    setUploading(true);
    try {
      const data = await filesApi.upload(file, 'business');
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (rulesStatus?.needsAcknowledgment) { setRulesOpen(true); return; }
    if (!form.title.trim() || !form.description.trim()) { toast({ title: 'Title & description required' }); return; }
    if (!form.estimatedValue || Number(form.estimatedValue) < 20) {
      toast({ title: 'Minimum item value is $20', description: 'Items under $20 can\'t be listed in the C.A.N. Auction Store.' });
      return;
    }
    if (form.images.length === 0) { toast({ title: 'Add at least one photo' }); return; }
    if (form.pickupInstructions.trim().length < 10) { toast({ title: 'Delivery or pickup instructions required', description: 'Tell the winner how they will get the item (at least 10 characters).' }); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        estimatedValue: Number(form.estimatedValue),
        images: form.images,
        deliveryMethod: form.deliveryMethod,
        deliveryFee: Number(form.deliveryFee || 0),
        deliveryDescription: form.deliveryDescription.trim(),
        pickupInstructions: form.pickupInstructions.trim(),
        testAuction: !!form.testAuction,
        shippingCost: Number(form.shippingCost || 0),
        taxes: Number(form.taxes || 0),
        handling: Number(form.handling || 0),
        productSize: form.productSize.trim(),
        productColor: form.productColor.trim(),
        productCondition: form.productCondition.trim(),
        productBrand: form.productBrand.trim(),
        productSpecs: form.productSpecs.trim(),
      };
      const result = await donate.submit(payload);
      if (result.status === 'active') {
        toast({ title: 'Listing approved!', description: `+100 pts earned. Winner pays $${(result.setPrice || 0).toFixed(2)}.` });
      } else if (result.status === 'pending_review') {
        toast({ title: 'Pending admin review', description: 'You\'ll be notified when it\'s live.' });
      } else {
        toast({ title: 'Listing rejected', description: result.moderationReason });
      }
      setForm({ title: '', description: '', estimatedValue: '', images: [], deliveryMethod: 'pickup', deliveryFee: '', deliveryDescription: '', pickupInstructions: '', testAuction: false, shippingCost: '', taxes: '', handling: '', productSize: '', productColor: '', productCondition: '', productBrand: '', productSpecs: '' });
      await refresh();
      await load();
    } catch (err) {
      if (err?.response?.status === 428) {
        setRulesOpen(true);
      } else {
        // Backend now returns a structured object for min-value violations.
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === 'object' ? (detail?.message || 'Try again') : (detail || 'Try again');
        toast({ title: 'Submission failed', description: msg });
      }
    } finally { setSubmitting(false); }
  };

  const badgeFor = (status) => {
    const map = {
      active: { c: 'bg-green-500/15 text-green-400 border-green-500/30', l: 'Live' },
      pending_review: { c: 'bg-amber-500/15 text-amber-400 border-amber-500/30', l: 'Pending review' },
      rejected: { c: 'bg-red-500/15 text-red-400 border-red-500/30', l: 'Rejected' },
      flagged: { c: 'bg-orange-500/15 text-orange-400 border-orange-500/30', l: 'Under review (flagged)' },
      sold: { c: 'bg-violet-500/15 text-violet-300 border-violet-500/30', l: 'Sold' },
      completed: { c: 'bg-blue-500/15 text-blue-300 border-blue-500/30', l: 'Completed' },
      deal_bin: { c: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', l: 'In Deal Bin' },
    };
    const m = map[status] || { c: 'bg-white/5 text-gray-400 border-white/10', l: status };
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${m.c}`}>{m.l}</span>;
  };

  return (
    <div className="space-y-8">
      <RulesModal open={rulesOpen} onAccept={acceptRules} onClose={rulesStatus?.needsAcknowledgment ? null : () => setRulesOpen(false)} />

      <div>
        <h1 className="text-2xl font-bold">Donate an item</h1>
        <p className="text-gray-400 text-sm mt-1">
          100% of proceeds fund the C.A.N. Capital Fund (Community + Youth 50/50 after Apr 2027). Donors earn <span className="text-green-400 font-semibold">100 pts</span> contribution reward when their listing is approved, plus another <span className="text-green-400 font-semibold">100 pts</span> when the sale is confirmed.
        </p>
      </div>

      <form onSubmit={submit} className="pbb-card p-5 space-y-4 max-w-2xl">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Item name</label>
          <input data-testid="donate-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="e.g. Signed vinyl record" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Description</label>
          <textarea data-testid="donate-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4} className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="Condition, provenance, dimensions, anything a buyer should know…" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><DollarSign size={12} /> Retail value (USD)</label>
            <input data-testid="donate-value" type="number" min="20" step="0.01" value={form.estimatedValue} onChange={(e) => setForm({ ...form, estimatedValue: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="150" />
            {(() => {
              const v = Number(form.estimatedValue);
              if (!form.estimatedValue || Number.isNaN(v) || v <= 0) return null;
              if (v < 20) {
                return (
                  <div className="text-[11px] text-red-400 mt-1" data-testid="donate-value-error">
                    Minimum item value is <span className="font-semibold">$20</span>. Items under $20 can't be listed in the C.A.N. Auction Store.
                  </div>
                );
              }
              const pct = v < 21 ? 0.50 : v <= 100 ? 0.40 : 0.30;
              const label = v < 21 ? '50% off ($20 or less)' : v <= 100 ? '60% off ($21–$100)' : '70% off ($101+)';
              return (
                <div className="text-[11px] text-gray-500 mt-1" data-testid="donate-value-preview">
                  Winner pays: <span className="text-emerald-300 font-semibold">${(v * pct).toFixed(2)}</span>
                  <span className="text-gray-600"> · {label}</span>
                </div>
              );
            })()}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><Truck size={12} /> Delivery method</label>
            <select data-testid="donate-delivery-method" value={form.deliveryMethod} onChange={(e) => setForm({ ...form, deliveryMethod: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm">
              {DELIVERY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* C.A.N. itemized cash costs the winner pays on top of the set price */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Shipping cost (USD)</label>
            <input data-testid="donate-shipping-cost" type="number" min="0" step="0.01" value={form.shippingCost} onChange={(e) => setForm({ ...form, shippingCost: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Sales tax (USD)</label>
            <input data-testid="donate-taxes" type="number" min="0" step="0.01" value={form.taxes} onChange={(e) => setForm({ ...form, taxes: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Handling fee (USD)</label>
            <input data-testid="donate-handling" type="number" min="0" step="0.01" value={form.handling} onChange={(e) => setForm({ ...form, handling: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
          </div>
        </div>
        {form.deliveryMethod === 'shipping' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Delivery fee (USD, buyer pays)</label>
              <input data-testid="donate-delivery-fee" type="number" min="0" step="0.01" value={form.deliveryFee} onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="12.50" />
            </div>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Delivery description</label>
          <input data-testid="donate-delivery-desc" value={form.deliveryDescription} onChange={(e) => setForm({ ...form, deliveryDescription: e.target.value })}
            className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
            placeholder={form.deliveryMethod === 'shipping' ? 'e.g. Ships USPS Priority within 2 business days from Atlanta' : 'e.g. Pickup at Buckhead location, weekdays 10-6'} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-300 font-semibold">Delivery or pickup instructions <span className="text-red-400">*</span></label>
          <textarea data-testid="donate-pickup-instructions" required minLength={10} maxLength={1000} rows={3} value={form.pickupInstructions}
            onChange={(e) => setForm({ ...form, pickupInstructions: e.target.value })}
            className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
            placeholder="e.g. Pick up at 123 Peachtree St, Atlanta, Mon–Fri 10am–6pm. Text (404) 555-0100 when you arrive. Or: I'll ship within 2 days to the address on your profile." />
          <p className="mt-1 text-[11px] text-gray-400">Private. Sent to the winner by email and text automatically after they pay.</p>
        </div>
        {user && (
          <label className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 cursor-pointer" data-testid="donate-test-auction-wrap">
            <input type="checkbox" checked={form.testAuction} onChange={(e) => setForm({ ...form, testAuction: e.target.checked })} className="mt-0.5 accent-yellow-500" data-testid="donate-test-auction"/>
            <span className="text-sm text-gray-100">Test Auction<span className="block text-[11px] text-gray-300">Ends 3 minutes after listing, regardless of the normal auction length. For testing the full win → checkout flow.</span></span>
          </label>
        )}

        {/* Product specifics (optional but recommended) */}
        <div className="pt-2 border-t border-[#1a1a1a]">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Product specifics <span className="text-gray-600 normal-case">· optional but recommended for buyers</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Size</label>
              <input data-testid="donate-size" value={form.productSize} onChange={(e) => setForm({ ...form, productSize: e.target.value })}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="M · Large · US 10 · 34x32" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Color</label>
              <input data-testid="donate-color" value={form.productColor} onChange={(e) => setForm({ ...form, productColor: e.target.value })}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="Emerald green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Condition</label>
              <input data-testid="donate-condition" value={form.productCondition} onChange={(e) => setForm({ ...form, productCondition: e.target.value })}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="New with tags · Gently used · Vintage" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500">Brand</label>
              <input data-testid="donate-brand" value={form.productBrand} onChange={(e) => setForm({ ...form, productBrand: e.target.value })}
                className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" placeholder="Nike · Levi's · Handmade" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-gray-500">Other details (dimensions, materials, quirks)</label>
            <textarea data-testid="donate-specs" value={form.productSpecs} onChange={(e) => setForm({ ...form, productSpecs: e.target.value })}
              rows={2} className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
              placeholder="24 × 36 in · cotton canvas · small paint scuff on back-left corner" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1"><ImageIcon size={12} /> Photos ({form.images.length}/5)</label>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {form.images.map((url, idx) => (
              <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-[#1f1f1f]">
                <img src={filesApi.urlFor(url)} alt={`img-${idx}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"><Trash2 size={12} /></button>
              </div>
            ))}
            {form.images.length < 5 && (
              <label data-testid="donate-add-photo" className="aspect-square rounded-lg border border-dashed border-[#2a2a2a] flex items-center justify-center text-gray-400 hover:text-green-400 hover:border-green-500/50 cursor-pointer">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={20} />}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
              </label>
            )}
          </div>
        </div>
        <div className="pt-2">
          <button data-testid="donate-submit" type="submit" disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-black font-semibold disabled:opacity-50">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <PackageOpen size={16} />} Submit donation
          </button>
          {rulesStatus?.needsAcknowledgment && (
            <span className="text-xs text-amber-400 ml-3">You'll be asked to accept the rules first.</span>
          )}
        </div>
      </form>

      {mine.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wider text-gray-500 font-semibold mb-3">Your donations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.map((it) => (
              <div key={it.id} data-testid={`donation-${it.id}`} className="pbb-card overflow-hidden flex flex-col h-full">
                <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<PackageOpen size={24}/>} />
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start gap-2 justify-between">
                    <div className="font-semibold text-sm min-w-0 truncate">{it.title}</div>
                    {badgeFor(it.status)}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Start: <span className="text-violet-300">{it.startingPrice} pts</span>
                    {(it.bids || 0) > 0 && <> · {it.bids} bids · current <span className="text-violet-300 font-semibold">{it.currentBid} pts</span></>}
                  </div>
                  {it.status === 'sold' && it.confirmationCode && (
                    <div className="mt-2 text-[11px] text-amber-400">Pickup code to receive from buyer: <span className="font-mono font-bold">{it.confirmationCode}</span></div>
                  )}
                  {it.moderationReason && (it.status === 'rejected' || it.status === 'pending_review') && (
                    <div className="mt-2 text-[11px] text-gray-500">{it.moderationReason}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <SiteSections pageKey="donate" />
    </div>
  );
}
